import {
  Button,
  Center,
  Group,
  Modal,
  SimpleGrid,
  Text,
  Title
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconAlertTriangle,
  IconChevronLeft,
  IconChevronRight,
  IconPlus,
  IconRefresh
} from '@tabler/icons-react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { StepSection } from '@/components/deployments/deployment-editor/StepSection'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { InfiniteScroll } from '@/components/ui-shared/InfiniteScroll/InfiniteScroll'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetDevicesInfinite } from '@/queries/deviceQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { DeviceRegistrationSteps } from '../../../devices/DeviceRegistrationSteps/DeviceRegistrationSteps'
import { SelectableDeviceCard } from './SelectableDeviceCard/SelectableDeviceCard'

type DeviceSelectionProps = {
  initialSelectedDeviceIds: string[]
  onGoBack: () => void
  onContinue: (selectedDeviceIds: string[]) => void
}

export const DeviceSelection = ({
  initialSelectedDeviceIds,
  onGoBack,
  onContinue
}: DeviceSelectionProps) => {
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>(
    initialSelectedDeviceIds
  )

  const [
    addDeviceModalOpened,
    { open: openAddDeviceModal, close: closeAddDeviceModal }
  ] = useDisclosure(false)

  const [canCreateDevice] = useHasPermissions(Resource.Devices, [
    CrudAction.Create
  ])

  const { data, isFetching, isError, refetch, fetchNextPage, hasNextPage } =
    useGetDevicesInfinite()

  const devices = data?.pages.flatMap((page) => page.results) ?? []

  const isOnlineDeviceSelected = devices.some(
    (device) => selectedDeviceIds.includes(device.id) && device.active
  )

  const isOfflineDeviceSelected = devices.some(
    (device) => selectedDeviceIds.includes(device.id) && !device.active
  )

  const handleLoadMore = () => {
    void fetchNextPage()
  }

  const handleOnReload = () => {
    void refetch()
  }

  const handleModalRefresh = () => {
    void refetch()
    closeAddDeviceModal()
  }

  const toggleDeviceSelection = (deviceId: string) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    )
  }

  const handleContinueClick = () => {
    if (selectedDeviceIds.length > 0) {
      onContinue(selectedDeviceIds)
    }
  }

  if (isError) {
    return (
      <ErrorWithReload onReload={handleOnReload}>
        <FormattedMessage id="devices.data.error" />
      </ErrorWithReload>
    )
  }

  return (
    <>
      <Group align="center" gap="lg" mb="md">
        <Title order={3}>
          <FormattedMessage id="deployments.step3" />
        </Title>

        <Group>
          {canCreateDevice && (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={openAddDeviceModal}
            >
              <FormattedMessage id="devices.list.buttons.create" />
            </Button>
          )}

          <Button
            variant="light"
            rightSection={<IconRefresh size={16} />}
            onClick={handleOnReload}
          >
            <FormattedMessage id="refresh" />
          </Button>
        </Group>
      </Group>

      <StepSection
        actions={
          <Group>
            <Button
              size="md"
              variant="outline"
              miw={200}
              leftSection={<IconChevronLeft size={16} stroke={3} />}
              onClick={onGoBack}
            >
              <FormattedMessage id="deployments.previousStep" />
            </Button>

            <Button
              miw={200}
              size="md"
              rightSection={<IconChevronRight size={16} stroke={3} />}
              disabled={!isOnlineDeviceSelected || isOfflineDeviceSelected}
              onClick={handleContinueClick}
            >
              <FormattedMessage id="deployments.nextStep" />
            </Button>
          </Group>
        }
      >
        {isOfflineDeviceSelected && (
          <Group gap="xs" wrap="nowrap" mb="sm">
            <IconAlertTriangle size={16} color="red" />

            <Text size="xs">
              <FormattedMessage id="devices.offlineDeviceSelected" />
            </Text>
          </Group>
        )}

        {devices.length === 0 && !isFetching && (
          <Center h={120}>
            <Text size="sm" fs="italic">
              <FormattedMessage id="devices.list.empty" />
            </Text>
          </Center>
        )}

        <InfiniteScroll
          hasMore={hasNextPage}
          isFetching={isFetching}
          onLoadMore={handleLoadMore}
        >
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing="xl">
            {devices.map((device) => (
              <SelectableDeviceCard
                key={device.id}
                name={device.label}
                isActive={device.active}
                isSelected={selectedDeviceIds.includes(device.id)}
                disabled={
                  !device.active && !selectedDeviceIds.includes(device.id)
                }
                onClick={() => toggleDeviceSelection(device.id)}
              />
            ))}
          </SimpleGrid>
        </InfiniteScroll>
      </StepSection>

      <Modal
        size="xl"
        title={<FormattedMessage id="devices.list.buttons.create" />}
        opened={addDeviceModalOpened}
        onClose={closeAddDeviceModal}
      >
        <DeviceRegistrationSteps onRefresh={handleModalRefresh} />
      </Modal>
    </>
  )
}
